import { getOntologyMappingsByConcept } from '@/services/database/documentIntelligence';
import type { DocumentOntologyCatalog } from '@/services/database/documentIntelligence';
import { RESEARCH_CONCEPTS } from '@/shared/document-intelligence/conceptRegistry';
import type { AuthoritativeConceptMapping, ResearchDomainId } from '@/shared/document-intelligence/researchOntology';

export interface DocumentConceptTextPage {
    page: number;
    text: string;
}

export interface DocumentConceptTextReference {
    context: string;
    end: number;
    id: string;
    page: number;
    start: number;
    text: string;
}

interface MatchedDocumentConceptBase {
    classification: 'canonical' | 'document-specific';
    description: string;
    id: string;
    label: string;
    ontologyVersion: string;
    references: DocumentConceptTextReference[];
}

export interface MatchedCanonicalDocumentConcept extends MatchedDocumentConceptBase {
    classification: 'canonical';
    mappings: readonly AuthoritativeConceptMapping[];
    primaryDomainId: ResearchDomainId;
    secondaryDomainIds: readonly ResearchDomainId[];
}

export interface MatchedDocumentSpecificConcept extends MatchedDocumentConceptBase {
    classification: 'document-specific';
    mappings: readonly [];
    primaryDomainId?: undefined;
    secondaryDomainIds: readonly [];
}

export type MatchedDocumentConcept = MatchedCanonicalDocumentConcept | MatchedDocumentSpecificConcept;

const STOP_WORDS = new Set(
    'about after again against also among and are because been before being between both but can could did does doing down during each few for from further had has have having here how into its may more most not now of off once only other our out over own remain remained remains same should some such than that the their them then there these they this those through too under until very was were what when where which while who will with would you your company companies including include includes fiscal quarter year years'.split(
        ' ',
    ),
);

export const DOCUMENT_WORD_PATTERN = /[\p{L}\p{N}]+(?:['’&.-][\p{L}\p{N}]+)*/gu;

const toConceptMapping = (mapping: DocumentOntologyCatalog['mappings'][number]): AuthoritativeConceptMapping => ({
    externalId: mapping.externalId,
    relation: mapping.relation,
    reviewedAt: mapping.reviewedAt,
    schemeId: mapping.schemeId,
    schemeVersion: mapping.schemeVersion,
    verification: mapping.verification,
});

const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getContext = (text: string, start: number, end: number) => {
    const contextStart = Math.max(text.lastIndexOf(' ', Math.max(start - 56, 0)), 0);
    const nextSpace = text.indexOf(' ', Math.min(end + 72, text.length));
    const contextEnd = nextSpace < 0 ? text.length : nextSpace;

    return `${contextStart > 0 ? '…' : ''}${text.slice(contextStart, contextEnd).trim()}${contextEnd < text.length ? '…' : ''}`;
};

const createReference = (
    conceptId: string,
    page: DocumentConceptTextPage,
    start: number,
    end: number,
    index: number,
): DocumentConceptTextReference => ({
    context: getContext(page.text, start, end),
    end,
    id: `${conceptId}:${page.page}:${index}`,
    page: page.page,
    start,
    text: page.text.slice(start, end),
});

const matchDefinition = (definition: (typeof RESEARCH_CONCEPTS)[number], pages: DocumentConceptTextPage[]) => {
    const id = definition.id;
    const expression = new RegExp(`\\b(?:${definition.patterns.map(escapePattern).join('|')})\\b`, 'giu');
    const references: DocumentConceptTextReference[] = [];

    pages.forEach((page) => {
        for (const match of page.text.matchAll(expression)) {
            references.push(createReference(id, page, match.index, match.index + match[0].length, references.length));
        }
    });

    return { definition, id, references };
};

const getDocumentTerms = (pages: DocumentConceptTextPage[], excludedTerms: Set<string>, ontologyVersion: string) => {
    const terms = new Map<string, { count: number; label: string; references: DocumentConceptTextReference[] }>();

    pages.forEach((page) => {
        for (const match of page.text.matchAll(DOCUMENT_WORD_PATTERN)) {
            const normalized = match[0].toLocaleLowerCase();

            if (
                normalized.length < 4 ||
                STOP_WORDS.has(normalized) ||
                excludedTerms.has(normalized) ||
                /^\d/.test(normalized)
            ) {
                continue;
            }

            const current = terms.get(normalized) ?? { count: 0, label: match[0], references: [] };
            const id = `document-term-${normalized}`;

            current.count += 1;
            current.references.push(
                createReference(id, page, match.index, match.index + match[0].length, current.references.length),
            );
            terms.set(normalized, current);
        }
    });

    return [...terms.entries()]
        .filter(([, term]) => term.count >= 2)
        .sort((left, right) => right[1].count - left[1].count || right[0].length - left[0].length)
        .slice(0, 5)
        .map(
            ([normalized, term]): MatchedDocumentSpecificConcept => ({
                classification: 'document-specific',
                description: 'A high-frequency term identified specifically within this document.',
                id: `document-term-${normalized}`,
                label: term.label,
                mappings: [],
                ontologyVersion,
                references: term.references,
                secondaryDomainIds: [],
            }),
        );
};

export const matchDocumentConcepts = (
    pages: DocumentConceptTextPage[],
    ontology: DocumentOntologyCatalog,
): MatchedDocumentConcept[] => {
    const mappingsByConcept = getOntologyMappingsByConcept(ontology);
    const rankedDefinitions = RESEARCH_CONCEPTS.map((definition) => matchDefinition(definition, pages))
        .filter((candidate) => candidate.references.length > 0)
        .sort(
            (left, right) =>
                right.definition.priority * 100 +
                right.references.length -
                (left.definition.priority * 100 + left.references.length),
        );
    const excludedTerms = new Set(rankedDefinitions.flatMap(({ definition }) => definition.patterns));
    const concepts = rankedDefinitions.map(
        ({ definition, id, references }): MatchedCanonicalDocumentConcept => ({
            classification: 'canonical',
            description: definition.description,
            id,
            label: definition.preferredLabel,
            mappings: (mappingsByConcept.get(definition.id) ?? []).map(toConceptMapping),
            ontologyVersion: ontology.version,
            primaryDomainId: definition.primaryDomainId,
            references,
            secondaryDomainIds: definition.secondaryDomainIds,
        }),
    );

    return [...concepts, ...getDocumentTerms(pages, excludedTerms, ontology.version)];
};
